import React from "react";
import { Badge, Box, Group, Text } from "@mantine/core";
import InfoTooltip from "../common/InfoTooltip";
import { ReportColumn } from "../../types/reports";
import { formatCellForDisplay } from "../../utils/reportUtils";
import styles from "./ReportBreakdownTable.module.css";

export interface ReportBreakdownTableProps {
  label: string;
  columns: ReportColumn[];
  rows: Array<Record<string, unknown>>;
  /** Indent under a parent row (default true). */
  indent?: boolean;
}

const isNumeric = (col: ReportColumn): boolean =>
  col.type === "currency" || col.type === "int" || col.type === "decimal";

/** Renders a cell; a row flagged `is_custom_rate` gets a "Custom" badge on
 * its rate column so per-service rates stand out from the default. */
const renderCell = (
  col: ReportColumn,
  row: Record<string, unknown>,
): React.ReactNode => {
  const value = row[col.key];
  if (value === null || value === undefined) return "—";
  if (col.key === "commission_rate" && row.is_custom_rate === true) {
    const label = String(value).replace(/\s*\(custom\)$/, "");
    return (
      <Group gap={6} wrap="nowrap">
        <Text size="sm">{label}</Text>
        <Badge size="xs" variant="light" color="teal">
          Custom
        </Badge>
      </Group>
    );
  }
  return formatCellForDisplay(value, col.type, col.accounting_negative);
};

const HeaderCell: React.FC<{ col: ReportColumn }> = ({ col }) => (
  <th className={isNumeric(col) ? styles.numeric : undefined}>
    <Group
      gap={4}
      wrap="nowrap"
      justify={isNumeric(col) ? "flex-end" : "flex-start"}
    >
      {col.label}
      {col.description && (
        <InfoTooltip ariaLabel={`About ${col.label}`} label={col.description} />
      )}
    </Group>
  </th>
);

/**
 * A drill-down table under an expanded row (Staff Earnings → "Commission by
 * service", Earnings Summary → service commissions). Columns come from the
 * backend; the table is indented under its row, sized to its content, and
 * only scrolls sideways when the screen is narrower than it.
 */
const ReportBreakdownTable: React.FC<ReportBreakdownTableProps> = ({
  label,
  columns,
  rows,
  indent = true,
}) => (
  <Box className={styles.wrap} pl={indent ? undefined : 0}>
    <Text className={styles.label}>{label}</Text>
    <Box className={styles.scroll}>
      <table className={styles.table} aria-label={label}>
        <thead>
          <tr>
            {columns.map((col) => (
              <HeaderCell key={col.key} col={col} />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={`${String(row[columns[0]?.key] ?? "")}-${i}`}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={isNumeric(col) ? styles.numeric : undefined}
                >
                  {renderCell(col, row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  </Box>
);

export default ReportBreakdownTable;
